/**
 * This class toggles the topbar search form's visibility on a button click
 *
 * @param { HTMLElement } container - The topbar search element itself
 * @param { HTMLElement } contentEl - The topbar search content element
 */
class TopbarSearch {
    isOpenAttribute = 'is-open';

    constructor(container, contentEl) {
        this.container = container;
        this.contentEl = contentEl;
        this.isOpen = false;

        // Get child elements
        this.button = this.container.querySelector('.topbar-search__button');
        this.form = this.contentEl.querySelector('.topbar-search-content__form');
        this.input = this.contentEl.querySelector('.topbar-search-content__input');
        this.submitButton = this.contentEl.querySelector('.topbar-search-content__submit');
        // Create variables for the focus trap
        this.focusableEls = [this.input, this.submitButton];
        this.focusedEl = null;

        // Warn if any of the elements is not found
        if (!this.button || !this.form || !this.input || !this.submitButton) {
            console.error('TOUJOU: Could not initialise the topbar search because some elements are missing!');
            return;
        }

        // Listen to button clicks to toggle the state
        this.button.addEventListener('click', () => {
            this.isOpen = !this._isOpen;
        })

        // Listen to keyboard clicks to we can close the search on ESC and so we can trap focus inside the element
        this.contentEl.addEventListener('click', (event) => {
            if (event.target === this.contentEl) {
              this.isOpen = false;
            }
        })

        window.addEventListener('conversion-search-bar-open', () => {
            this.isOpen = false;
        })
    }

    /**
     * When the isOpen changes to true:
     *  - toggle isOpenAttribute
     *  - focus on input
     *  - start listening to key up events
     *
     * When it changes to false:
     *  - Stop listening to keyup events to improve performance
     *
     * @param { Boolean } value
     */
    set isOpen(value) {
        if (value) {
            this.contentEl.setAttribute(this.isOpenAttribute, '');
            this.input.focus();
            this.focusedEl = this.input;
            window.addEventListener('keyup', this._handleTopbarSearchKeyup.bind(this));
            this.container.dispatchEvent(new CustomEvent('topbar-search-open'));
        } else {
            this.contentEl.removeAttribute(this.isOpenAttribute);
            window.removeEventListener('keyup', this._handleTopbarSearchKeyup);
        }

        this._isOpen = value;
    }

    get isOpen() {
        return this._isOpen;
    }

    /**
     *
     * - Listen to ESC press so thatwe can close the element
     * - Listen to TAB press so that we can trap the focus inside the search element
     *
     * @private
     * @param { Event } event
     */
    _handleTopbarSearchKeyup(event) {
        if (event.key === 'Escape') {
            this.isOpen = false;
        } else if (event.key === 'Tab') {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation()
            const focusedElIndex = this.focusableEls.indexOf(this.focusedEl);
            this.focusedEl = this.focusableEls[(focusedElIndex + 1) % this.focusableEls.length];
            this.focusedEl.focus();
        }
    }
}

/**
 * Get element and create an instance of the TopbarSearch class
 */
export function initTopbarSearch() {
    const topbarSearch = document.querySelector('.topbar-search');
    const topbarSearchContent = document.querySelector('.topbar-search-content');

    if (!topbarSearch || !topbarSearchContent) return;

    topbarSearch.topbarSearch = new TopbarSearch(topbarSearch, topbarSearchContent);
}
